import React, { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { 
  X, 
  Search, 
  Grid,
  List,
  Info,
  AtSign,
  Package,
  Clock,
  Star,
  AlertCircle,
  Gamepad2,
  Trophy,
  Zap,
  Sword,
  Shield,
  Users,
  Percent,
  TrendingUp,
  TrendingDown
} from "lucide-react";
import { useTranslation } from "react-i18next";

import gameActivationService from "../../../../services/gameActivationService";

import SuccessModal from "../../RechargeTopup/components/SuccessModal";
import ErrorModal from "../../productList/components/ErrorModal";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
    "& .MuiPaper-root": {
      width: "900px",
      maxWidth: "95vw",
      border: `1px solid ${isDark ? "#444" : "#d32f2f"}`,
      borderRadius: 16,
      padding: theme.spacing(3),
      backgroundColor: isDark ? "#111827" : theme.palette.background.paper,
    },
  };
});

const getGameCategoryIcon = (category) => {
  const icons = {
    'action': <Sword className="text-red-600 dark:text-red-400" size={20} />,
    'adventure': <Shield className="text-primary-600 dark:text-primary-400" size={20} />,
    'rpg': <Trophy className="text-yellow-600 dark:text-yellow-400" size={20} />,
    'strategy': <Zap className="text-primary-600 dark:text-primary-400" size={20} />,
    'sports': <Gamepad2 className="text-green-600 dark:text-green-400" size={20} />,
    'casual': <Users className="text-secondary-600 dark:text-secondary-400" size={20} />
  };
  return icons[category?.toLowerCase()] || <Gamepad2 size={20} />;
};

const getAccountPlaceholder = (gameType, t) => {
  const placeholders = {
    'pubg': t('gameActivationModal.placeholders.pubg'),
    'freefire': t('gameActivationModal.placeholders.freefire'),
    'cod': t('gameActivationModal.placeholders.cod'),
    'clashofclans': t('gameActivationModal.placeholders.clashofclans'),
    'mlbb': t('gameActivationModal.placeholders.mlbb'),
    'fifa': t('gameActivationModal.placeholders.fifa'),
    'genshin': t('gameActivationModal.placeholders.genshin')
  };
  return placeholders[gameType?.toLowerCase()] || t('gameActivationModal.placeholders.default');
};

const validateGameAccount = (account, gameType, t) => {
  if (!account || !account.trim()) {
    return { isValid: false, message: t('gameActivationModal.validation.accountRequired') };
  }

  const trimmed = account.trim();

  if (trimmed.length < 3) {
    return { isValid: false, message: t('gameActivationModal.validation.minLength') };
  }

  if (trimmed.length > 50) {
    return { isValid: false, message: t('gameActivationModal.validation.maxLength') };
  }

  if (/[<>{}[\]\\\/]/.test(trimmed)) {
    return { isValid: false, message: t('gameActivationModal.validation.invalidChars') };
  }

  return { isValid: true, message: "" };
};

export default function GameActivationModal({ open, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [gameAccount, setGameAccount] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isTransferSuccess, setIsTransferSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activationResponse, setActivationResponse] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [filters, setFilters] = useState({
    minPrice: "",
    maxPrice: "",
    sortBy: "popularity"
  });
  const IMG_BASE_URL = import.meta.env.VITE_IMG_BASE_URL2;

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const { data: countriesData } = useQuery({
    queryKey: ["countries"],
    queryFn: () => gameActivationService.getCountries(),
    enabled: open,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["gameProductCategories"],
    queryFn: () => gameActivationService.getGameProductCategories(),
    enabled: open,
  });

  const { data: typesData } = useQuery({
    queryKey: ["gameProductTypes"],
    queryFn: () => gameActivationService.getGameProductTypes(),
    enabled: open,
  });

  const { 
    data: productsData, 
    isLoading: productsLoading,
    refetch: refetchProducts 
  } = useQuery({
    queryKey: ["gameProducts", selectedCountry?.id, selectedCategory?.id, selectedType?.id, searchQuery, filters],
    queryFn: () => gameActivationService.getGameProducts({
      countryId: selectedCountry?.id,
      productCategoryId: selectedCategory?.id,
      productTypeId: selectedType?.id,
      search: searchQuery,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      sortBy: filters.sortBy
    }),
    enabled: open && !!selectedCountry?.id,
  });

  const activateMutation = useMutation({
    mutationFn: (payload) => gameActivationService.activateGameProduct(payload),
    onSuccess: (data) => {
      setActivationResponse(data);
      setIsTransferSuccess(true);
      toast.success(t('gameActivationModal.toasts.activationSuccess'));
    },
    onError: (error) => {
      console.error("Activation error:", error);
      const message = error?.response?.data?.error || t('gameActivationModal.toasts.activationFailed');
      setErrorMessage(message);
      setIsError(true);
      toast.error(message);
    },
  });

  useEffect(() => {
    if (countriesData?.data?.length > 0 && !selectedCountry) {
      const afghanistan = countriesData.data.find(c => c.countryCode === "AF") || countriesData.data[0];
      setSelectedCountry(afghanistan);
    }
  }, [countriesData]);

  useEffect(() => {
    if (categoriesData?.data?.length > 0 && !selectedCategory) {
      setSelectedCategory(categoriesData.data[0]);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (typesData?.data?.length > 0 && !selectedType) {
      setSelectedType(typesData.data[0]);
    }
  }, [typesData]);

  const handleReset = () => {
    setStep(1);
    setSelectedProduct(null);
    setGameAccount("");
    setValidationError("");
    setSearchQuery("");
    setActivationResponse(null);
    setShowPriceBreakdown(false);
    setFilters({
      minPrice: "",
      maxPrice: "",
      sortBy: "popularity"
    });
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setStep(2);
  };

  const handleAccountChange = (e) => {
    const value = e.target.value;
    setGameAccount(value);
    
    if (value.trim()) {
      const validation = validateGameAccount(value, selectedProduct?.gameType, t);
      setValidationError(validation.isValid ? "" : validation.message);
    } else {
      setValidationError("");
    }
  };

  const handleNext = () => {
    if (step === 2) {
      if (!gameAccount.trim()) {
        setValidationError(t('gameActivationModal.validation.accountRequired'));
        return;
      }
      
      const validation = validateGameAccount(gameAccount, selectedProduct?.gameType, t);
      if (!validation.isValid) {
        setValidationError(validation.message);
        return;
      }
      
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleActivate = () => {
    if (!selectedProduct || !gameAccount.trim()) {
      toast.error(t('gameActivationModal.toasts.selectProduct'));
      return;
    }

    const payload = {
      productId: selectedProduct.id,
      receiver: gameAccount.trim(),
      countryId: selectedCountry?.id,
    };

    activateMutation.mutate(payload);
  };

  const closeSuccessModal = () => {
    setIsTransferSuccess(false);
    onSuccess();
    handleReset();
    onClose();
  };

  const closeErrorModal = () => {
    setIsError(false);
    setErrorMessage("");
  };

  const getDisplayPrice = (product) => {
    return product.price || product.originalPrice || product.basePrice || 0;
  };

  const getPriceBreakdown = (product) => {
    if (product.priceBreakdown) {
      return product.priceBreakdown;
    }
    return null;
  };

  const getFilteredProducts = () => {
    if (!productsData?.data) return [];
    
    let filtered = [...productsData.data];
    
    if (filters.minPrice) {
      filtered = filtered.filter(p => parseFloat(getDisplayPrice(p)) >= parseFloat(filters.minPrice));
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(p => parseFloat(getDisplayPrice(p)) <= parseFloat(filters.maxPrice));
    }
    
    switch (filters.sortBy) {
      case 'price_low':
        filtered.sort((a, b) => parseFloat(getDisplayPrice(a)) - parseFloat(getDisplayPrice(b)));
        break;
      case 'price_high':
        filtered.sort((a, b) => parseFloat(getDisplayPrice(b)) - parseFloat(getDisplayPrice(a)));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        filtered.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
    }
    
    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  return (
    <>
      <StyledDialog open={open} onClose={onClose}>
        <div className="px-5 py-3">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className={`text-xl font-semibold transition-colors duration-300 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {step === 1 && t('gameActivationModal.step1.title')}
                {step === 2 && t('gameActivationModal.step2.title')}
                {step === 3 && t('gameActivationModal.step3.title')}
              </h3>
              <p className={`text-sm mt-1 transition-colors duration-300 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {step === 1 && t('gameActivationModal.step1.subtitle')}
                {step === 2 && t('gameActivationModal.step2.subtitle', { product: selectedProduct?.productName?.en || t('gameActivationModal.selectedProduct') })}
                {step === 3 && t('gameActivationModal.step3.subtitle')}
              </p>
            </div>
            <button
              onClick={onClose}
              className={`transition-colors ${
                isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <X size={24} />
            </button>
          </div>

          {step === 1 && (
            <div className="space-y-6">
              <div className={`p-4 rounded-lg space-y-4 transition-colors duration-300 ${
                isDark ? 'bg-gray-800' : 'bg-gray-50'
              }`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('gameActivationModal.filters.country')}
                    </label>
                    <select
                      value={selectedCountry?.id || ""}
                      onChange={(e) => {
                        const country = countriesData?.data?.find(c => c.id === e.target.value);
                        setSelectedCountry(country);
                      }}
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      {countriesData?.data?.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.countryName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('gameActivationModal.filters.category')}
                    </label>
                    <select
                      value={selectedCategory?.id || ""}
                      onChange={(e) => {
                        const category = categoriesData?.data?.find(c => c.id === e.target.value);
                        setSelectedCategory(category);
                      }}
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      {categoriesData?.data?.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.categoryName?.en || category.categoryName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('gameActivationModal.filters.gameType')}
                    </label>
                    <select
                      value={selectedType?.id || ""}
                      onChange={(e) => {
                        const type = typesData?.data?.find(t => t.id === e.target.value);
                        setSelectedType(type);
                      }}
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      {typesData?.data?.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.typeName?.en || type.typeName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('gameActivationModal.filters.sortBy')}
                    </label>
                    <select
                      value={filters.sortBy}
                      onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="popularity">{t('gameActivationModal.sortOptions.popularity')}</option>
                      <option value="price_low">{t('gameActivationModal.sortOptions.priceLow')}</option>
                      <option value="price_high">{t('gameActivationModal.sortOptions.priceHigh')}</option>
                      <option value="newest">{t('gameActivationModal.sortOptions.newest')}</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('gameActivationModal.filters.minPrice')}
                    </label>
                    <input
                      type="number"
                      value={filters.minPrice}
                      onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value }))}
                      placeholder="0"
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('gameActivationModal.filters.maxPrice')}
                    </label>
                    <input
                      type="number"
                      value={filters.maxPrice}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                      placeholder="10000"
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('gameActivationModal.filters.search')}
                    </label>
                    <div className="relative">
                      <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                        isDark ? 'text-gray-500' : 'text-gray-400'
                      }`} size={16} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('gameActivationModal.filters.searchPlaceholder')}
                        className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 transition-colors duration-300 ${
                          isDark 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <p className={`text-sm transition-colors duration-300 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {t('gameActivationModal.productsCount', { count: filteredProducts.length })}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === "grid" 
                          ? "bg-primary-600 text-white" 
                          : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      <Grid size={18} />
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === "list" 
                          ? "bg-primary-600 text-white" 
                          : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      <List size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {productsLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                  <p className={`mt-4 transition-colors duration-300 ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {t('gameActivationModal.loading')}
                  </p>
                </div>
              ) : filteredProducts.length > 0 ? (
                viewMode === "grid" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto p-1">
                    {filteredProducts.map((product) => {
                      const displayPrice = getDisplayPrice(product);
                      const originalPrice = product.originalPrice || product.basePrice;
                      const hasDiscount = originalPrice && displayPrice < originalPrice;
                      const priceBreakdown = getPriceBreakdown(product);
                      
                      return (
                        <div
                          key={product.id}
                          onClick={() => handleProductSelect(product)}
                          className={`border rounded-xl p-4 cursor-pointer transition-all group ${
                            isDark 
                              ? 'border-gray-700 hover:border-primary-600 hover:shadow-lg' 
                              : 'border-gray-200 hover:border-primary-600 hover:shadow-lg'
                          }`}
                        >
                          {product.image && (
                            <img src={`${IMG_BASE_URL}/${product.image}`} className="rounded-[6px] mb-2 w-full h-32 object-cover" alt="Product" />
                          )}
                        
                          <h4 className={`font-semibold mb-1 transition-colors duration-300 group-hover:text-primary-600 ${
                            isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                            {product.productName?.en || product.productName}
                          </h4>
                          
                          <div className="flex items-center justify-between mb-2">
                            <div>
                           
                              <span className="text-lg font-bold text-primary-600">
                                {parseFloat(displayPrice).toFixed(2)} AFN
                              </span>
                            </div>
                            <div className={`flex items-center gap-2 text-xs transition-colors duration-300 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              <Clock size={14} />
                              <span>{product.deliveryTime || t('gameActivationModal.instant')}</span>
                            </div>
                          </div>

                      
                          {product.minQuantity && product.maxQuantity && (
                            <div className={`mt-2 text-xs transition-colors duration-300 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {t('gameActivationModal.quantity')}: {product.minQuantity} - {product.maxQuantity}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto p-1">
                    {filteredProducts.map((product) => {
                      const displayPrice = getDisplayPrice(product);
                      const originalPrice = product.originalPrice || product.basePrice;
                      const hasDiscount = originalPrice && displayPrice < originalPrice;
                      const priceBreakdown = getPriceBreakdown(product);
                      
                      return (
                        <div
                          key={product.id}
                          onClick={() => handleProductSelect(product)}
                          className={`border rounded-lg p-4 cursor-pointer transition-all flex items-center gap-4 ${
                            isDark 
                              ? 'border-gray-700 hover:border-primary-600 hover:shadow-md' 
                              : 'border-gray-200 hover:border-primary-600 hover:shadow-md'
                          }`}
                        >
                          {product.image && (
                            <img src={`${IMG_BASE_URL}/${product.image}`} className="rounded-[6px] w-[100px] h-[100px] object-cover" alt="Product" />
                          )}
                        
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className={`font-semibold transition-colors duration-300 ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}>
                                {product.productName?.en || product.productName}
                              </h4>
                              {product.popularity > 80 && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  isDark 
                                    ? 'bg-yellow-900/30 text-yellow-300' 
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {t('gameActivationModal.popular')}
                                </span>
                              )}
                            </div>
                            
                            <p className={`text-sm mb-2 transition-colors duration-300 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {product.description || t('gameActivationModal.noDescription')}
                            </p>
                            
                            <div className="flex items-center gap-4 text-sm">
                              <div>
                               
                                <span className="font-bold text-primary-600">
                                  {parseFloat(displayPrice).toFixed(2)} AFN
                                </span>
                              </div>
                              <span className={`flex items-center gap-1 transition-colors duration-300 ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                <Clock size={14} />
                                {product.deliveryTime || t('gameActivationModal.instant')}
                              </span>
                           
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                <div className="text-center py-12">
                  <Package className={`mx-auto mb-3 ${
                    isDark ? 'text-gray-600' : 'text-gray-300'
                  }`} size={48} />
                  <p className={`transition-colors duration-300 ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {t('gameActivationModal.noProducts')}
                  </p>
                  <p className={`text-sm mt-1 transition-colors duration-300 ${
                    isDark ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    {t('gameActivationModal.adjustFilters')}
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 2 && selectedProduct && (
            <div className="space-y-6">
              <div className={`bg-gradient-to-r from-primary-50 to-secondary-50 p-4 rounded-lg ${
                isDark ? 'from-primary-900/20 to-secondary-900/20' : ''
              }`}>
                <div className="flex items-start gap-4">
                  {selectedProduct.image && (
                    <img src={`${IMG_BASE_URL}/${selectedProduct.image}`} className="rounded-[6px] w-[100px] h-[100px] object-cover" alt="Product" />
                  )}
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-primary-800/50' : 'bg-primary-200'}`}>
                    {getGameCategoryIcon(selectedProduct.category)}
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {selectedProduct.productName?.en || selectedProduct.productName}
                    </h4>
                    <p className={`text-sm mt-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {selectedProduct.description || t('gameActivationModal.noDescription')}
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                     
                      <span className="text-lg font-bold text-primary-600">
                        {parseFloat(selectedProduct.price || selectedProduct.originalPrice).toFixed(2)} AFN
                      </span>
                      <span className={`text-sm flex items-center gap-1 transition-colors duration-300 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <Clock size={14} />
                        {selectedProduct.deliveryTime || t('gameActivationModal.instantDelivery')}
                      </span>
                    </div>
                    
              
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('gameActivationModal.gameAccount')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                      isDark ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      <AtSign size={18} />
                    </div>
                    <input
                      type="text"
                      value={gameAccount}
                      onChange={handleAccountChange}
                      placeholder={getAccountPlaceholder(selectedProduct.gameType, t)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      autoFocus
                    />
                  </div>
                  {validationError ? (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {validationError}
                    </p>
                  ) : (
                    <p className={`text-sm mt-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('gameActivationModal.accountHelpText')}
                    </p>
                  )}
                </div>

                <div className={`p-4 rounded-lg ${
                  isDark ? 'bg-primary-900/20' : 'bg-primary-50'
                }`}>
                  <h5 className={`font-medium mb-2 flex items-center gap-2 ${
                    isDark ? 'text-primary-300' : 'text-primary-800'
                  }`}>
                    <Info size={16} />
                    {t('gameActivationModal.instructions.title')}
                  </h5>
                  <ul className={`text-sm space-y-1 list-disc pl-5 ${
                    isDark ? 'text-primary-300' : 'text-primary-700'
                  }`}>
                    <li>{t('gameActivationModal.instructions.li1')}</li>
                    <li>{t('gameActivationModal.instructions.li2')}</li>
                    <li>{t('gameActivationModal.instructions.li3')}</li>
                    <li>{t('gameActivationModal.instructions.li4')}</li>
                    <li>{t('gameActivationModal.instructions.li5')}</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {step === 3 && selectedProduct && (
            <div className="space-y-6">
              <div className={`rounded-lg p-6 ${
                isDark ? 'bg-gray-800' : 'bg-gray-50'
              }`}>
                <h4 className={`font-semibold mb-4 transition-colors duration-300 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {t('gameActivationModal.orderSummary')}
                </h4>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div className={`p-2 rounded-lg ${
                      isDark ? 'bg-primary-900/30' : 'bg-primary-100'
                    }`}>
                      {getGameCategoryIcon(selectedProduct.category)}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium transition-colors duration-300 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {selectedProduct.productName?.en || selectedProduct.productName}
                      </p>
                      <p className={`text-sm mt-1 transition-colors duration-300 ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {selectedProduct.description || t('gameActivationModal.gameProduct')}
                      </p>
                    </div>
                    <div className="text-right">
                    
                      <p className="font-bold text-primary-600">
                        {parseFloat(selectedProduct.price || selectedProduct.originalPrice).toFixed(2)} AFN
                      </p>
                    </div>
                  </div>

                  <div className={`flex justify-between items-center py-3 border-b ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <span className={`transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {t('gameActivationModal.gameAccount')}:
                    </span>
                    <span className={`font-medium flex items-center gap-2 transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      <AtSign size={16} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                      {gameAccount}
                    </span>
                  </div>

                  <div className={`flex justify-between items-center py-3 border-b ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <span className={`transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {t('gameActivationModal.delivery')}:
                    </span>
                    <span className={`font-medium flex items-center gap-1 transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      <Clock size={16} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                      {selectedProduct.deliveryTime || t('gameActivationModal.instant')}
                    </span>
                  </div>

                  <div className="flex justify-between items-center pt-4">
                    <span className={`text-lg font-semibold transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {t('gameActivationModal.totalAmount')}:
                    </span>
                    <span className="text-2xl font-bold text-primary-600">
                      {parseFloat(selectedProduct.price || selectedProduct.originalPrice).toFixed(2)} AFN
                    </span>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-lg ${
                isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`flex-shrink-0 mt-0.5 ${
                    isDark ? 'text-yellow-400' : 'text-yellow-600'
                  }`} size={18} />
                  <div>
                    <h5 className={`font-medium mb-2 ${
                      isDark ? 'text-yellow-300' : 'text-yellow-800'
                    }`}>
                      {t('gameActivationModal.pleaseNote')}
                    </h5>
                    <ul className={`text-sm space-y-1 list-disc pl-5 ${
                      isDark ? 'text-yellow-300' : 'text-yellow-700'
                    }`}>
                      <li>{t('gameActivationModal.note.li1')}</li>
                      <li>{t('gameActivationModal.note.li2')}</li>
                      <li>{t('gameActivationModal.note.li3')}</li>
                      <li>{t('gameActivationModal.note.li4')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <button
                onClick={handleBack}
                className={`flex-1 py-2.5 px-4 border rounded-lg transition-colors font-medium ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('common.back')}
              </button>
            )}
            
            {step === 1 ? (
              <button
                onClick={onClose}
                className={`flex-1 py-2.5 px-4 border rounded-lg transition-colors font-medium ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('common.cancel')}
              </button>
            ) : step === 2 ? (
              <button
                onClick={handleNext}
                disabled={!gameAccount.trim() || !!validationError}
                className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-colors ${
                  !gameAccount.trim() || !!validationError
                    ? "bg-gray-300 dark:bg-gray-600 cursor-not-allowed"
                    : "bg-primary-600 hover:bg-primary-700 text-white"
                }`}
              >
                {t('common.continue')}
              </button>
            ) : (
              <button
                onClick={handleActivate}
                disabled={activateMutation.isPending}
                className="flex-1 py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                {activateMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('common.processing')}
                  </span>
                ) : (
                  t('gameActivationModal.confirmActivate')
                )}
              </button>
            )}
          </div>
        </div>
      </StyledDialog>

      <SuccessModal
        open={isTransferSuccess}
        onClose={closeSuccessModal}
        amount={selectedProduct?.price || selectedProduct?.originalPrice}
        title={t('gameActivationModal.success.title')}
        message={
          activationResponse?.message || 
          t('gameActivationModal.success.message')
        }
        transactionId={activationResponse?.txnNumber}
      />

      <ErrorModal
        open={isError}
        onClose={closeErrorModal}
        title={t('gameActivationModal.error.title')}
        error={errorMessage}
        onRetry={handleActivate}
      >
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">{t('gameActivationModal.product')}:</span>
            <span className="font-medium dark:text-white">{selectedProduct?.productName?.en}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">{t('gameActivationModal.gameAccount')}:</span>
            <span className="font-medium dark:text-white">{gameAccount}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">{t('gameActivationModal.amount')}:</span>
            <span className="font-medium text-primary-600 dark:text-primary-400">
              {parseFloat(selectedProduct?.price || selectedProduct?.originalPrice || 0).toFixed(2)} AFN
            </span>
          </div>
        </div>
      </ErrorModal>
    </>
  );
}